import { SkeletonTable } from '../../components/LoadingSkeleton/LoadingSkeleton';
import { splitPhone } from '../../utils/phone';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TrashIcon from '../../components/icons/TrashIcon';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUsers,
  fetchDealerUsers,
  addUserAsync,
  editUserAsync,
  deleteUserAsync,
  clearMessages,
  selectUsers,
  selectUsersLoading,
  selectUsersError,
  selectSuccessMessage,
  selectRefreshUsersData,
  setRefreshUsersData
} from '../../features/technicians/technicianSlice';
import { fetchDealers } from '../../features/dealers/dealerSlice';
import './technician.css';
const digitsOnly = (value, maxLength) => value.replace(/\D/g, '').slice(0, maxLength);
const preventNonNumericInput = (event) => {
  if (event.ctrlKey || event.metaKey || ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  if (!/^\d$/.test(event.key)) event.preventDefault();
};

const Technicians = () => {
  const dispatch = useDispatch();
  const { users: allUsers, loading } = useSelector((state) => state.technicians);
  const successMessage = useSelector(selectSuccessMessage);
  const errorMessage = useSelector(selectUsersError);
  const refreshData = useSelector(selectRefreshUsersData);

  // Get user role from Redux/auth state
  const { user } = useSelector((state) => state.auth);
  const { dealers } = useSelector((state) => state.dealers);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [goToPage, setGoToPage] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Modal states for Create/Edit
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Access levels
  const accessLevels = ['Manager', 'Technician'];

  // Form Data for User
  const [userFormData, setUserFormData] = useState({
    userName: '',
    userEmail: '',
    userMobile1: '',
    userMobile2: '',
    userhome: '+1',
    userwork: '+1',
    accesslevel: 'Technician',
    dealerId: '',
    userPassword: ''
  });

  const loadUsers = useCallback(() => {
    if (user?.role === 'Admin' || user?.role === 'superadmin') {
      dispatch(fetchUsers());
      dispatch(fetchDealers());
    } else if (user?.dealerId) dispatch(fetchDealerUsers(user.dealerId));
  }, [dispatch, user]);

  useEffect(() => {
    loadUsers();
    if (user?.dealerId) setUserFormData(prev => ({ ...prev, dealerId: user.dealerId }));
  }, [loadUsers, user?.dealerId]);

  useEffect(() => {
    if (!refreshData) return;
    loadUsers();
    dispatch(setRefreshUsersData(false));
  }, [refreshData, loadUsers, dispatch]);

  // Handle success/error messages
  useEffect(() => {
    if (successMessage) {
      setAlertMessage(successMessage);
      setAlertType('success');
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
        dispatch(clearMessages());
      }, 3000);
    }
    if (errorMessage) {
      setAlertMessage(errorMessage);
      setAlertType('error');
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
        dispatch(clearMessages());
      }, 3000);
    }
  }, [successMessage, errorMessage, dispatch]);

  const getInitials = (name) => {
    if (!name) return '??';
    const words = name.trim().split(/\s+/);
    return `${words[0]?.[0] || ''}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase();
  };

  const avatarColors = [
    '#2b6e9e', '#7c3aed', '#f59e0b', '#ef4444',
    '#10b981', '#f97316', '#8b5cf6', '#00b894'
  ];

  const getAvatarColor = (name) => {
    if (!name) return '#2b6e9e';
    return avatarColors[name.charCodeAt(0) % avatarColors.length];
  };

  const getDealerName = (user) => {
    return user.dealerName || user.dealer || 'N/A';
  };

  // User records are not consistent: older records return `dealer` or
  // `dealerName`, while newer ones return `dealerId`.  The select needs the
  // dealer ID, so resolve any of those values before opening the edit form.
  const getDealerId = (technician) => {
    const savedDealer = technician.dealerId || technician.dealerid || technician.dealer || technician.dealerName || '';
    const matchingDealer = dealers?.find(dealer =>
      String(dealer.dealerId) === String(savedDealer) ||
      String(dealer.dealerName || '').trim().toLowerCase() === String(savedDealer).trim().toLowerCase()
    );

    return matchingDealer?.dealerId || savedDealer;
  };

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    return allUsers.filter(user =>
      [user.userName, user.userEmail, user.userMobile1, getDealerName(user)]
        .some(field => field && field.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [allUsers, searchTerm]);

  // Reset to page 1 when search or rows per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, rowsPerPage]);

  // Calculate pagination values
  const totalItems = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Get current page items
  const getCurrentPageItems = useCallback(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
    return filteredUsers.slice(startIndex, endIndex);
  }, [currentPage, rowsPerPage, filteredUsers, totalItems]);

  const currentItems = getCurrentPageItems();

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setGoToPage('');
      // Scroll to top of table
      const tableElement = document.querySelector('.table-scroll');
      if (tableElement) {
        tableElement.scrollTop = 0;
      }
    }
  }, [totalPages]);

  const handleRowsPerPageChange = useCallback((e) => {
    const newRowsPerPage = Number(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    setGoToPage('');
  }, []);

  const handleGoToPage = useCallback((e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setGoToPage('');
    }
  }, [goToPage, totalPages]);

  // Generate page numbers for display - FIXED
  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, currentPage + halfVisible);

      // Adjust if we're at the beginning
      if (currentPage - halfVisible < 1) {
        endPage = Math.min(totalPages, endPage + (halfVisible - currentPage + 1));
      }
      
      // Adjust if we're at the end
      if (currentPage + halfVisible > totalPages) {
        startPage = Math.max(1, startPage - (currentPage + halfVisible - totalPages));
      }

      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add last page and ellipsis if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  // Calculate displayed range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  const toggleExpand = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  // Handle Add User
  const handleAddUser = () => {
    setUserFormData({
      userName: '',
      userEmail: '',
      userMobile1: '',
      userMobile2: '',
      userhome: '+1',
      userwork: '+1',
      accesslevel: 'Technician',
      dealerId: user?.role === 'Admin' || user?.role === 'superadmin' ? '' : user?.dealerId || '',
      userPassword: ''
    });
    setIsAddModalOpen(true);
  };

  // Handle Edit User
  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      userName: user.userName || '',
      userEmail: user.userEmail || '',
      userMobile1: splitPhone(user.userMobile1).number,
      userhome: splitPhone(user.userMobile1).code,
      userMobile2: splitPhone(user.userMobile2).number,
      userwork: splitPhone(user.userMobile2).code,
      accesslevel: user.accesslevel || 'Technician',
      dealerId: getDealerId(user),
      userPassword: user.userPassword || user.userpassword || user.password || ''
    });
    setIsEditModalOpen(true);
  };

  // Submit Add User
  const handleAddSubmit = async (e) => {
    e.preventDefault();

    const userData = {
      userName: userFormData.userName,
      userEmail: userFormData.userEmail,
      userMobile1: userFormData.userMobile1 ? userFormData.userhome + userFormData.userMobile1 : '',
      userMobile2: userFormData.userMobile2 ? userFormData.userwork + userFormData.userMobile2 : '',
      accesslevel: userFormData.accesslevel,
      dealer: userFormData.dealerId,
      userPassword: userFormData.userPassword
    };

    await dispatch(addUserAsync(userData));
    setIsAddModalOpen(false);
    resetForm();
  };

  // Submit Edit User
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const userData = {
      userName: userFormData.userName,
      userEmail: userFormData.userEmail,
      userMobile1: userFormData.userMobile1 ? userFormData.userhome + userFormData.userMobile1 : '',
      userMobile2: userFormData.userMobile2 ? userFormData.userwork + userFormData.userMobile2 : '',
      accesslevel: userFormData.accesslevel,
      dealer: userFormData.dealerId,
    };

    if (userFormData.userPassword) {
      userData.userPassword = userFormData.userPassword;
    }

    await dispatch(editUserAsync({
      userId: editingUser.userId,
      userData: userData
    }));
    setIsEditModalOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setUserFormData({
      userName: '',
      userEmail: '',
      userMobile1: '',
      userMobile2: '',
      userhome: '+1',
      userwork: '+1',
      accesslevel: 'Technician',
      dealerId: '',
      userPassword: ''
    });
    setEditingUser(null);
    setShowPassword(false);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const userToDelete = selectedUser;
    setIsDeleteModalOpen(false);
    setSelectedUser(null);

    if (userToDelete) {
      await dispatch(deleteUserAsync(userToDelete.userId));
    }
  };

  // Expanded Row Component
  const ExpandedRow = ({ user, isOpen }) => {
    if (!isOpen) return null;

    const detailItems = [
      { icon: '📋', label: 'ID', value: user.userId },
      { icon: '👤', label: 'Name', value: user.userName },
      { icon: '✉️', label: 'Email', value: user.userEmail },
      { icon: '📱', label: 'Phone', value: user.userMobile1 },
      { icon: '📞', label: 'Phone 2', value: user.userMobile2 || 'N/A' },
      { icon: '🔑', label: 'Access Level', value: user.accesslevel || 'N/A' },
      { icon: '🏢', label: 'Dealer', value: getDealerName(user) },
    ];

    return (
      <tr className="expanded-row">
        <td colSpan="8">
          <div className="expanded-content">
            <div className="expanded-header">
              <div className="expanded-header-icon">👤</div>
              <div>
                <h3 className="expanded-header-title">Technician Details</h3>
                <div className="expanded-header-subtitle">
                  Complete information for {user.userName}
                </div>
              </div>
            </div>
            <div className="expanded-list-view">
              <div className="expanded-two-columns">
                {detailItems.map((item, idx) => (
                  <div key={idx} className="expanded-list-item">
                    <div className="list-item-icon">{item.icon}</div>
                    <div className="list-item-label">{item.label}:</div>
                    <div className="list-item-value">
                      {item.value && item.value !== 'undefined' ? item.value : 'N/A'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  // User Card Component for Mobile View
  const UserCard = ({ user, index }) => {
    const [showDetails, setShowDetails] = useState(false);

    const detailItems = [
      { icon: '📋', label: 'ID', value: user.userId },
      { icon: '📞', label: 'Phone 2', value: user.userMobile2 || 'N/A' },
      { icon: '🔑', label: 'Access Level', value: user.accesslevel || 'N/A' },
      { icon: '🏢', label: 'Dealer', value: getDealerName(user) },
    ];

    return (
      <div className="technician-card-wrapper">
        <div className="technician-card">
          <div className="card-header">
            <div className="card-avatar-wrap">
              <div className="avatar" style={{ background: getAvatarColor(user.userName) }}>
                {getInitials(user.userName)}
              </div>
              <div className="card-identity">
                <span className="card-name">{user.userName}</span>
                <span className="card-dealer">{getDealerName(user)}</span>
              </div>
            </div>
            <div className="card-actions-header">
              <button className="card-action-icon edit" title="Edit" onClick={() => handleEditUser(user)}>
                <svg viewBox="0 0 18 18" fill="currentColor" width="14" height="14">
                  <path d="M12.5 2.5L15.5 5.5L6 15H3V12L12.5 2.5Z" stroke="currentColor" fill="none" strokeWidth="1.2" />
                  <path d="M10.5 4.5L13.5 7.5" stroke="currentColor" strokeWidth="1.2" />
                </svg>
              </button>
              <button className="card-action-icon delete" title="Delete" onClick={() => handleDeleteClick(user)}>
                <TrashIcon />
              </button>
              <button
                className={`card-more-btn ${showDetails ? 'active' : ''}`}
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? 'Less' : 'More'}
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="card-row">
              <span className="card-label">Email</span>
              <span className="card-value email">{user.userEmail || 'N/A'}</span>
            </div>
            <div className="card-row">
              <span className="card-label">Phone</span>
              <span className="card-value phone">{user.userMobile1 || 'N/A'}</span>
            </div>
          </div>

          {showDetails && (
            <div className="card-expanded-details">
              <div className="card-expanded-section">
                <h4>👤 Technician Information</h4>
                <div className="card-details-grid">
                  {detailItems.map((item, idx) => (
                    <div key={idx} className="card-detail-item">
                      <div className="card-detail-label">{item.icon} {item.label}</div>
                      <div className="card-detail-value">
                        {item.value && item.value !== 'undefined' ? item.value : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading && allUsers.length === 0) {
    return (
      <div className="technicians-root">
        <div className="technicians-wrapper">
          <SkeletonTable title="Technicians" headings={["SNO", "Name", "Email", "Phone", "Dealer Name", "Actions", "Delete", "More"]} />
        </div>
      </div>
    );
  }

  return (
    <div className="technicians-root">
      <div className="technicians-wrapper">
        {/* Alert Banner */}
        {showAlert && (
          <div className={`alert-banner ${alertType}`}>
            <span>{alertMessage}</span>
            <button className="alert-close" onClick={() => setShowAlert(false)}>✕</button>
          </div>
        )}

        <div className="page-header">
          <div className="header-left">
            <h1 className="page-title">TECHNICIANS</h1>
          </div>
          <div className="header-right">
            <div className="stat-pill">
              <span className="stat-dot" />
              <span className="stat-label">Records</span>
              <span className="stat-value">{filteredUsers.length}</span>
            </div>
            {(user?.role === 'Admin' || user?.role === 'superadmin' || user?.role === 'dealer') && (
              <button className="add-btn" onClick={handleAddUser}>+ Add Technician</button>
            )}
          </div>
        </div>

        <div className="toolbar">
          <div className="search-wrap">
            <svg className="search-icon" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8" />
              <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <input
              className="search-input"
              type="text"
              placeholder="Search technicians..."
              value={searchTerm}
              onChange={(e) => { 
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchTerm && (
              <button className="search-clear" onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}>✕</button>
            )}
          </div>
          <div className="rows-control">
            <span>Show</span>
            <select 
              value={rowsPerPage} 
              onChange={handleRowsPerPageChange}
            >
              {[5, 10, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">⊘</div>
            <p>No technicians found</p>
            {searchTerm && (
              <button className="reset-btn" onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}>Clear search</button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop/Laptop Table View */}
            <div className="table-view">
              <div className="table-card">
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>SNO</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Dealer Name</th>
                        <th className="text-center">Actions</th>
                        <th className="text-center">Delete</th>
                        <th className="text-center">More</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((user, idx) => {
                        const rowIndex = (currentPage - 1) * rowsPerPage + idx;
                        const isExpanded = expandedRow === rowIndex;
                        return (
                          <React.Fragment key={user.userId || idx}>
                            <tr className="data-row">
                              <td className="row-index">{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                              <td className="name-cell">
                                <div className="name-with-avatar">
                                  <div className="mini-avatar" style={{ background: getAvatarColor(user.userName) }}>
                                    {getInitials(user.userName)}
                                  </div>
                                  <span className="technician-name">{user.userName}</span>
                                </div>
                              </td>
                              <td className="email-cell">
                                <span className="email-address">{user.userEmail || 'N/A'}</span>
                              </td>
                              <td className="phone-cell">
                                <span className="phone-number">{user.userMobile1 || 'N/A'}</span>
                              </td>
                              <td className="dealer-cell">
                                <span className="dealer-name">{getDealerName(user)}</span>
                              </td>
                              <td>
                                <button className="action-icon edit-icon" title="Edit" onClick={() => handleEditUser(user)}>
                                  <svg viewBox="0 0 18 18" fill="currentColor" width="14" height="14">
                                    <path d="M12.5 2.5L15.5 5.5L6 15H3V12L12.5 2.5Z" stroke="currentColor" fill="none" strokeWidth="1.2" />
                                    <path d="M10.5 4.5L13.5 7.5" stroke="currentColor" strokeWidth="1.2" />
                                  </svg>
                                </button>
                              </td>
                              <td>
                                <button className="action-icon delete-icon" title="Delete" onClick={() => handleDeleteClick(user)}>
                                  <TrashIcon />
                                </button>
                              </td>
                              <td className="more-cell">
                                <button
                                  className={`more-btn ${isExpanded ? 'active' : ''}`}
                                  onClick={() => toggleExpand(rowIndex)}
                                >
                                  {isExpanded ? 'Less' : 'More'}
                                </button>
                              </td>
                            </tr>
                            <ExpandedRow user={user} isOpen={isExpanded} />
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="pagination-bar">
                  <div className="pagination-info">
                    Showing <strong>{startItem}–{endItem}</strong> of <strong>{totalItems}</strong>
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(1)} 
                      disabled={currentPage === 1}
                    >
                      «
                    </button>
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(currentPage - 1)} 
                      disabled={currentPage === 1}
                    >
                      ‹
                    </button>
                    {getPageNumbers().map((p, i) => {
                      if (p === '...') {
                        return <span key={`ellipsis-${i}`} className="pg-dots">…</span>;
                      }
                      return (
                        <button 
                          key={`page-${p}`} 
                          className={`pg-btn ${currentPage === p ? 'active' : ''}`} 
                          onClick={() => handlePageChange(p)}
                        >
                          {p}
                        </button>
                      );
                    })}
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(currentPage + 1)} 
                      disabled={currentPage === totalPages}
                    >
                      ›
                    </button>
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(totalPages)} 
                      disabled={currentPage === totalPages}
                    >
                      »
                    </button>
                  </div>
                  <div className="goto-wrap">
                    <span>Go to</span>
                    <form onSubmit={handleGoToPage}>
                      <input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={goToPage}
                        onChange={e => setGoToPage(e.target.value)}
                        placeholder="—"
                      />
                      <button type="submit">↵</button>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* Tablet/Mobile Card View */}
            <div className="card-view">
              <div className="cards-grid">
                {currentItems.map((user, idx) => (
                  <UserCard key={user.userId || idx} user={user} index={idx} />
                ))}
              </div>
              <div className="pagination-bar mobile-pagination">
                <div className="pagination-info">
                  {startItem}-{endItem} of {totalItems}
                </div>
                <div className="pagination-controls">
                  <button 
                    className="pg-btn" 
                    onClick={() => handlePageChange(currentPage - 1)} 
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  <span className="page-current">{currentPage}/{totalPages}</span>
                  <button 
                    className="pg-btn" 
                    onClick={() => handlePageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Technician Modal */}
      {isAddModalOpen && (
        <div className="modal-overlay" onClick={() => setIsAddModalOpen(false)}>
          <div className="modal-container add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Technician</h2>
              <button className="modal-close" onClick={() => setIsAddModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleAddSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    autoComplete="off"
                    value={userFormData.userEmail}
                    onChange={(e) => {
                      const userEmail = e.target.value;
                      setUserFormData({
                        ...userFormData,
                        userEmail,
                        userName: userEmail.split('@')[0]
                      });
                    }}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={userFormData.userName}
                    onChange={(e) => setUserFormData({ ...userFormData, userName: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password <span className="required">*</span></label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={userFormData.userPassword}
                      onChange={(e) => setUserFormData({ ...userFormData, userPassword: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone <span className="required">*</span></label>
                  <div className="phone-input-group">
                    <select
                      className="country-code"
                      value={userFormData.userhome}
                      onChange={(e) => setUserFormData({ ...userFormData, userhome: e.target.value })}
                    >
                      <option value="+1">+1 (US)</option>
                      <option value="+91">+91 (IN)</option>
                      <option value="+44">+44 (UK)</option>
                    </select>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={userFormData.userMobile1}
                      onChange={(e) => setUserFormData({ ...userFormData, userMobile1: digitsOnly(e.target.value, 10) })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone 2</label>
                  <div className="phone-input-group">
                    <select
                      className="country-code"
                      value={userFormData.userwork}
                      onChange={(e) => setUserFormData({ ...userFormData, userwork: e.target.value })}
                    >
                      <option value="+1">+1 (US)</option>
                      <option value="+91">+91 (IN)</option>
                      <option value="+44">+44 (UK)</option>
                    </select>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={userFormData.userMobile2}
                      onChange={(e) => setUserFormData({ ...userFormData, userMobile2: digitsOnly(e.target.value, 10) })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Access Level <span className="required">*</span></label>
                  <select
                    value={userFormData.accesslevel}
                    onChange={(e) => setUserFormData({ ...userFormData, accesslevel: e.target.value })}
                    required
                  >
                    {accessLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                {(user?.role === 'Admin' || user?.role === 'superadmin') && (
                  <div className="form-group">
                    <label>Dealer <span className="required">*</span></label>
                    <select
                      value={userFormData.dealerId}
                      onChange={(e) => setUserFormData({ ...userFormData, dealerId: e.target.value })}
                      required
                    >
                      <option value="">Select Dealer</option>
                      {dealers && dealers.map((dealer) => (
                        <option key={dealer.dealerId} value={dealer.dealerId}>
                          {dealer.dealerName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn">SAVE</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Technician Modal */}
      {isEditModalOpen && editingUser && (
        <div className="modal-overlay" onClick={() => setIsEditModalOpen(false)}>
          <div className="modal-container edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Technician</h2>
              <button className="modal-close" onClick={() => setIsEditModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={userFormData.userName}
                    onChange={(e) => setUserFormData({ ...userFormData, userName: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    value={userFormData.userEmail}
                    onChange={(e) => setUserFormData({ ...userFormData, userEmail: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={userFormData.userPassword}
                      onChange={(e) => setUserFormData({ ...userFormData, userPassword: e.target.value })}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone <span className="required">*</span></label>
                  <div className="phone-input-group">
<select className="country-code" value={userFormData.userhome} onChange={(e) => setUserFormData({ ...userFormData, userhome: e.target.value })}><option value="+1">+1</option><option value="+91">+91</option><option value="+44">+44</option></select>
<input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={userFormData.userMobile1}
                    onKeyDown={preventNonNumericInput}
                    onChange={(e) => setUserFormData({ ...userFormData, userMobile1: digitsOnly(e.target.value, 10) })}
                    required
                  />
</div>
                </div>

                <div className="form-group">
                  <label>Phone 2</label>
                  <div className="phone-input-group">
<select className="country-code" value={userFormData.userwork} onChange={(e) => setUserFormData({ ...userFormData, userwork: e.target.value })}><option value="+1">+1</option><option value="+91">+91</option><option value="+44">+44</option></select>
<input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={userFormData.userMobile2}
                    onKeyDown={preventNonNumericInput}
                    onChange={(e) => setUserFormData({ ...userFormData, userMobile2: digitsOnly(e.target.value, 10) })}
                  />
</div>
                </div>

                <div className="form-group">
                  <label>Access Level <span className="required">*</span></label>
                  <select
                    value={userFormData.accesslevel}
                    onChange={(e) => setUserFormData({ ...userFormData, accesslevel: e.target.value })}
                    required
                  >
                    {accessLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                {(user?.role === 'Admin' || user?.role === 'superadmin') && (
                  <div className="form-group">
                    <label>Dealer <span className="required">*</span></label>
                    <select
                      value={userFormData.dealerId}
                      onChange={(e) => setUserFormData({ ...userFormData, dealerId: e.target.value })}
                      required
                    >
                      <option value="">Select Dealer</option>
                      {dealers && dealers.map((dealer) => (
                        <option key={dealer.dealerId} value={dealer.dealerId}>
                          {dealer.dealerName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn">UPDATE</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedUser && (
        <div className="modal-overlay" onClick={() => setIsDeleteModalOpen(false)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-content">
              <div className="delete-icon">🗑️</div>
              <h3>Are you sure? You want to delete Technician - {selectedUser.userName}</h3>
              <p>You can't revert your action</p>
              <div className="delete-modal-actions">
                <button className="delete-confirm-btn" onClick={handleConfirmDelete}>Yes Delete it!</button>
                <button className="delete-cancel-btn" onClick={() => setIsDeleteModalOpen(false)}>No, Keep it!</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Technicians;
